import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { userAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const roles = ['admin', 'manager', 'viewer'];

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
      });
    } else {
      setSelectedUser(null);
      setFormData({ name: '', email: '', password: '', role: 'admin' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormData({ name: '', email: '', password: '', role: 'admin' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!selectedUser && !formData.password.trim()) {
      toast.error('Password is required for new users');
      return;
    }

    setSaving(true);
    try {
      if (selectedUser) {
        await userAPI.update(selectedUser.id, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
        });
        toast.success('User updated successfully');
      } else {
        await userAPI.create(formData);
        toast.success('User created successfully');
      }
      fetchUsers();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    if (selectedUser.id === currentUser?.id) {
      toast.error("You cannot delete your own account");
      setDeleteDialogOpen(false);
      return;
    }

    try {
      await userAPI.delete(selectedUser.id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="users-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-medium text-[#1A1A1A] tracking-tight">
              User Management
            </h1>
            <p className="text-[#585858] mt-2">
              Manage admin users and their access levels
            </p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8] font-medium uppercase tracking-wide rounded-sm"
            data-testid="add-user-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add User
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E6DCD3] rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#69111F]" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-[#585858]">
              <p>No users found.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F2EBE3] hover:bg-[#F2EBE3]">
                  <TableHead className="font-serif text-[#69111F] font-medium">Name</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Email</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Role</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Created</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="hover:bg-[#FDFBF8]">
                    <TableCell className="font-medium text-[#1A1A1A]">
                      {user.name}
                      {user.id === currentUser?.id && (
                        <span className="ml-2 text-xs text-[#69111F]">(You)</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[#585858]">{user.email}</TableCell>
                    <TableCell>
                      <span className="px-3 py-1 bg-[#F2EBE3] text-[#69111F] text-sm rounded-sm capitalize">
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-[#585858]">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(user)}
                          className="text-[#69111F] hover:bg-[#69111F]/10"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(user)}
                          disabled={user.id === currentUser?.id}
                          className="text-red-600 hover:bg-red-50 disabled:opacity-50"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-[#1A1A1A]">
                {selectedUser ? 'Edit User' : 'Create User'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#1A1A1A]">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    className="border-[#E6DCD3] focus:border-[#69111F]"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1A1A1A]">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="border-[#E6DCD3] focus:border-[#69111F]"
                    data-testid="user-email-input"
                  />
                </div>
                {!selectedUser && (
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-[#1A1A1A]">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter password"
                      className="border-[#E6DCD3] focus:border-[#69111F]"
                      data-testid="user-password-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-[#1A1A1A]">Role</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="border-[#E6DCD3]" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role} value={role} className="capitalize">{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  className="border-[#E6DCD3]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8]"
                  data-testid="save-user-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {selectedUser ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-[#1A1A1A]">
                Delete User
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#585858]">
                Are you sure you want to delete "{selectedUser?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#E6DCD3]">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-user-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default Users;
